/**
 * Expansión de un evento de calendario (potencialmente recurrente) en
 * sus ocurrencias concretas dentro de un rango [fromIso, toIso].
 *
 * Convención de días de la semana: 0 = Lunes, 6 = Domingo.
 *
 * Caso base (sin recurrence): devuelve la ocurrencia template tal cual
 * si toca el rango.
 *
 * Recurrence:
 *  - daily        — todos los días desde start hasta recurrence_until (o ∞).
 *                   Si recurrence_days está poblado, filtra a esos weekdays.
 *  - weekly       — cada N días donde weekday ∈ recurrence_days.
 *  - biweekly     — igual a weekly pero cada 2 semanas, contando desde la
 *                   semana del start_date.
 *  - monthly      — el mismo día de mes que start_date, cada mes.
 *  - yearly       — la misma fecha (MM-DD) cada año.
 *
 * Cada ocurrencia mantiene la duración del template (end - start).
 */

export type CalendarRecurrence = "daily" | "weekly" | "biweekly" | "monthly" | "yearly";

export interface RecurringEventInput {
  startDate: string;
  endDate: string;
  recurrence?: CalendarRecurrence | null;
  recurrenceDays?: number[] | null;
  recurrenceUntil?: string | null;
}

export interface ExpandedOccurrence {
  startDate: string;
  endDate: string;
}

/** Días entre dos fechas YYYY-MM-DD (ignorando hora). */
function daysBetween(aIso: string, bIso: string): number {
  const a = new Date(`${aIso}T00:00:00Z`);
  const b = new Date(`${bIso}T00:00:00Z`);
  return Math.round((b.getTime() - a.getTime()) / 86400000);
}

function isoOf(d: Date): string {
  return d.toISOString().slice(0, 10);
}

function dowMon(d: Date): number {
  // getUTCDay: 0=Sun ... 6=Sat → convertimos a 0=Mon ... 6=Sun
  return (d.getUTCDay() + 6) % 7;
}

export function expandRecurringEvent(
  ev: RecurringEventInput,
  fromIso: string,
  toIso: string,
): ExpandedOccurrence[] {
  const dur = daysBetween(ev.startDate, ev.endDate);
  if (dur < 0) return [];
  const cap = ev.recurrenceUntil ? new Date(`${ev.recurrenceUntil}T00:00:00Z`) : null;
  const to = new Date(`${toIso}T00:00:00Z`);
  const from = new Date(`${fromIso}T00:00:00Z`);
  const limit = cap && cap < to ? cap : to;

  // Caso sin recurrencia: emitir si toca el rango.
  if (!ev.recurrence) {
    const start = new Date(`${ev.startDate}T00:00:00Z`);
    const end = new Date(`${ev.endDate}T00:00:00Z`);
    if (end < from || start > to) return [];
    return [{ startDate: ev.startDate, endDate: ev.endDate }];
  }

  const out: ExpandedOccurrence[] = [];
  const allowedDays = ev.recurrenceDays?.length ? new Set(ev.recurrenceDays) : null;
  const startTemplate = new Date(`${ev.startDate}T00:00:00Z`);
  // Hard cap de iteraciones — protege contra rangos muy grandes.
  const MAX_ITER = 1500;

  if (ev.recurrence === "daily" || ev.recurrence === "weekly" || ev.recurrence === "biweekly") {
    const step = ev.recurrence === "biweekly" ? 14 : 1;
    // Para weekly/biweekly avanzamos día por día y filtramos por weekday.
    // (Más simple que calcular saltos al primer matching weekday.)
    const stride = ev.recurrence === "daily" ? 1 : ev.recurrence === "biweekly" ? 1 : 1;
    const weekIndex = (d: Date): number =>
      Math.floor((d.getTime() - startTemplate.getTime()) / (7 * 86400000));
    let cur = new Date(startTemplate);
    let i = 0;
    while (cur <= limit && i++ < MAX_ITER) {
      if (cur >= from || daysBetween(cur.toISOString().slice(0, 10), toIso) >= -dur) {
        // Check day filter
        if (!allowedDays || allowedDays.has(dowMon(cur))) {
          // Check biweekly: weekIndex relativo al start debe ser par.
          let emit = true;
          if (ev.recurrence === "biweekly") {
            if (weekIndex(cur) % 2 !== 0) emit = false;
          }
          if (emit) {
            const occEnd = new Date(cur);
            occEnd.setUTCDate(occEnd.getUTCDate() + dur);
            // Emit solo si toca el rango.
            if (!(occEnd < from || cur > to)) {
              out.push({ startDate: isoOf(cur), endDate: isoOf(occEnd) });
            }
          }
        }
      }
      cur.setUTCDate(cur.getUTCDate() + stride);
    }
    void step;
    return out;
  }

  if (ev.recurrence === "monthly") {
    const cur = new Date(startTemplate);
    let i = 0;
    while (cur <= limit && i++ < MAX_ITER) {
      const occEnd = new Date(cur);
      occEnd.setUTCDate(occEnd.getUTCDate() + dur);
      if (!(occEnd < from || cur > to)) {
        out.push({ startDate: isoOf(cur), endDate: isoOf(occEnd) });
      }
      // Avanzar un mes manteniendo el día. Si el día no existe en el
      // siguiente mes (ej. 31 ene → feb), JS lo desborda automáticamente
      // a marzo — preferimos clampear al último día del mes destino.
      const desiredDay = startTemplate.getUTCDate();
      cur.setUTCDate(1);
      cur.setUTCMonth(cur.getUTCMonth() + 1);
      const lastDayOfTargetMonth = new Date(
        Date.UTC(cur.getUTCFullYear(), cur.getUTCMonth() + 1, 0),
      ).getUTCDate();
      cur.setUTCDate(Math.min(desiredDay, lastDayOfTargetMonth));
    }
    return out;
  }

  if (ev.recurrence === "yearly") {
    const cur = new Date(startTemplate);
    let i = 0;
    while (cur <= limit && i++ < MAX_ITER) {
      const occEnd = new Date(cur);
      occEnd.setUTCDate(occEnd.getUTCDate() + dur);
      if (!(occEnd < from || cur > to)) {
        out.push({ startDate: isoOf(cur), endDate: isoOf(occEnd) });
      }
      cur.setUTCFullYear(cur.getUTCFullYear() + 1);
    }
    return out;
  }

  return out;
}
